import { Controller } from "@hotwired/stimulus"

// Applies conditional visibility on the custom-report fill-in form.
//
// Each conditional element carries `data-conditions` (JSON array of
// { field_key, operator, value }) and `data-conditions-match` ("all" | "any").
// Fields are keyed by `data-field-key`; blocks by `data-block-id`. As source
// inputs change, we re-evaluate every element and show/hide its wrapper.
//
// Operator semantics MIRROR app/services/custom_reports/condition_evaluator.rb —
// keep the two in sync. Exceptions, emitted only by the timesheet km gate
// (ConfigurableTimeSheetsHelper#manual_distance_gate_conditions): the
// "unchecked" operator and the `first_entry` rule flag.
//
// The input name prefix is configurable so the same controller drives both the
// custom-report form (`custom_report[field_data]`) and the configurable
// timesheet form (`custom_fields`). A source field's value is read from
// `${namePrefix}[${key}]`.
export default class extends Controller {
  static values = {
    namePrefix: { type: String, default: "custom_report[field_data]" },
  }

  connect() {
    this.collectElements()

    this.boundApply = this.apply.bind(this)
    this.element.addEventListener("input", this.boundApply)
    this.element.addEventListener("change", this.boundApply)

    // Cross-scope sources: a per-entry scope may reference a form-level field
    // (and vice versa — see the fieldValue fallback), whose inputs live outside
    // this.element, so their events never bubble through it. Listen at document
    // level for those; in-scope events are already covered above.
    this.boundDocumentApply = (event) => {
      if (this.element.contains(event.target)) return
      if (!this.watchesEventTarget(event.target)) return
      if (this.crossScopeApplyScheduled) return
      this.crossScopeApplyScheduled = true
      requestAnimationFrame(() => {
        this.crossScopeApplyScheduled = false
        this.apply()
      })
    }
    document.addEventListener("input", this.boundDocumentApply)
    document.addEventListener("change", this.boundDocumentApply)

    // Adding or removing a whole entry block changes what the cross-block
    // fallback (findFallbackInputGroups) can see, but it fires no input/change
    // and happens outside this.element for a form-level scope — so neither
    // listener above nor the observer below would notice, and a field conditional
    // on a per-entry key would keep the visibility of a block that is gone.
    this.boundRefresh = () => {
      this.collectElements()
      this.applyAll()
    }
    document.addEventListener("dynamic-form:refresh", this.boundRefresh)

    // Content injected after connect (Turbo streams, nested-form row templates,
    // prefill actions) must be re-collected and re-evaluated: a snapshot from
    // connect() alone would never hide an injected conditional element, and
    // inputs added inside an already-hidden wrapper must be disabled. Only
    // childList is observed, so our own class/disabled toggles cannot re-fire
    // the observer; rescans are coalesced per animation frame.
    this.observer = new MutationObserver(() => {
      if (this.rescanScheduled) return
      this.rescanScheduled = true
      requestAnimationFrame(() => {
        this.rescanScheduled = false
        this.collectElements()
        this.applyAll()
      })
    })
    this.observer.observe(this.element, { childList: true, subtree: true })

    this.applyAll()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.boundDocumentApply) {
      document.removeEventListener("input", this.boundDocumentApply)
      document.removeEventListener("change", this.boundDocumentApply)
    }
    if (this.boundRefresh) document.removeEventListener("dynamic-form:refresh", this.boundRefresh)
    if (!this.boundApply) return
    this.element.removeEventListener("input", this.boundApply)
    this.element.removeEventListener("change", this.boundApply)
  }

  collectElements() {
    this.elements = Array.from(this.element.querySelectorAll("[data-conditions]"))
      .map((el) => {
        let rules
        try {
          rules = JSON.parse(el.dataset.conditions || "[]")
        } catch {
          rules = []
        }
        if (!Array.isArray(rules) || rules.length === 0) return null
        return { el, rules, match: el.dataset.conditionsMatch || "all" }
      })
      .filter(Boolean)

    // Every key this scope's rules read. Events on anything else cannot change
    // what we render, so the document-level listener drops them without work.
    this.sourceKeys = new Set(
      this.elements.flatMap((entry) => entry.rules.map((rule) => rule.field_key)),
    )
  }

  // Whether an event outside this scope could change one of our source values.
  // A multi-block form runs one instance per block, so without this every
  // keystroke anywhere would re-run every instance's full fixpoint.
  watchesEventTarget(target) {
    const name = target?.name
    if (!name) return false

    return Array.from(this.sourceKeys).some(
      (key) => name.endsWith(`[${key}]`) || name.endsWith(`[${key}][]`),
    )
  }

  // Unconditionally sync every conditional element once — apply() only touches
  // elements whose visibility flips, which would leave inputs injected into an
  // already-hidden wrapper enabled — then settle cascades via the fixpoint.
  applyAll() {
    this.elements.forEach((entry) => this.setVisible(entry.el, this.evaluate(entry)))
    this.apply()
  }

  apply() {
    // Iterate to a fixpoint so chained/cascaded conditions resolve regardless of
    // declaration order (a hidden source counts as blank for its dependents).
    const maxPasses = this.elements.length + 1
    for (let pass = 0; pass < maxPasses; pass++) {
      let changed = false
      this.elements.forEach((entry) => {
        const shouldShow = this.evaluate(entry)
        const isHidden = entry.el.classList.contains("hidden")
        if (shouldShow === isHidden) {
          this.setVisible(entry.el, shouldShow)
          changed = true
        }
      })
      if (!changed) break
    }
  }

  setVisible(el, visible) {
    el.classList.toggle("hidden", !visible)
    // Disable inputs inside hidden elements so HTML5 `required` does not block
    // submit; keep their values (re-enabled when shown again). Showing a wrapper
    // leaves the inputs of a nested conditional that stays hidden disabled —
    // apply() never revisits an element whose own visibility did not flip. Only
    // elements this controller manages count: other `hidden` toggles are not ours.
    const hiddenNested = visible
      ? this.elements
          .map((entry) => entry.el)
          .filter(
            (other) => other !== el && el.contains(other) && other.classList.contains("hidden"),
          )
      : []
    el.querySelectorAll("input, select, textarea").forEach((input) => {
      input.disabled = !visible || hiddenNested.some((other) => other.contains(input))
    })
  }

  evaluate(entry) {
    const results = entry.rules.map((rule) => this.ruleMatches(rule))
    return entry.match === "any" ? results.some(Boolean) : results.every(Boolean)
  }

  // A rule holds when ANY of the source field's candidate values satisfies it.
  // In-scope there is exactly one candidate, so this only differs across scopes:
  // a form-level field conditioned on a per-entry key is shown as soon as one
  // entry block matches, because the server requires it for that block's record.
  ruleMatches(rule) {
    // "unchecked": a checkbox that is shown and not ticked (stored as "false").
    // Unlike "blank", a hidden or absent field never matches — the server drops
    // hidden answers, so it would not count as "false" there either.
    if (rule.operator === "unchecked") {
      return this.fieldValues(rule.field_key, [], rule.first_entry === true).some(
        (stored) => !this.isPresent(stored),
      )
    }

    const candidates = this.fieldValues(rule.field_key, [null], rule.first_entry === true)
    return candidates.some((stored) => this.storedMatches(stored, rule))
  }

  storedMatches(stored, rule) {
    const expected = rule.value

    switch (rule.operator) {
      case "present":
        return this.isPresent(stored)
      case "blank":
        return !this.isPresent(stored)
      case "not_equals":
        return !this.valuesEqual(stored, expected)
      case "contains":
        return this.valueContains(stored, expected)
      default: // "equals"
        return this.valuesEqual(stored, expected)
    }
  }

  // Reads the current value(s) of a source field, treating a field whose own
  // wrapper is hidden as blank (mirrors visible_with_ancestors? cascade).
  //
  // Cross-scope fallback: when the key does not resolve inside this scope, it is
  // looked up across the whole form — matched by name SUFFIX, because per-entry
  // fields are prefixed per block (`additional_entries[1][custom_fields][k]`,
  // `site_allocations[0][custom_fields][k]`) and a multi-site form has no bare
  // `custom_fields[k]` input at all, not even for the first allocation. Each
  // block that carries the key contributes one candidate value. +missing+ is
  // what a hidden or absent field yields (blank by default). +firstEntry+ reads
  // only the first block (primary entry / first allocation): the day-level km
  // is booked on that record, and the server checks its gate there alone.
  fieldValues(key, missing = [null], firstEntry = false) {
    const wrapper = this.element.querySelector(`[data-field-key="${CSS.escape(key)}"]`)
    if (wrapper && wrapper.classList.contains("hidden")) return missing

    const inputs = this.findSourceInputs(this.element, this.namePrefixValue, key)
    if (inputs.length > 0) return [this.readInputsValue(inputs)]

    const groups = this.findFallbackInputGroups(key)
    const candidates = (firstEntry ? groups.slice(0, 1) : groups)
      .filter((group) => {
        const groupWrapper = group[0].closest("[data-field-key]")
        return !(groupWrapper && groupWrapper.classList.contains("hidden"))
      })
      .map((group) => this.readInputsValue(group))

    return candidates.length > 0 ? candidates : missing
  }

  // Inputs for `key` anywhere in this form, grouped by the scope prefix that
  // owns them, in document order (so the first group is the primary entry /
  // first allocation). Scoped to the form rather than the document: nothing
  // constrains a same-named input on an unrelated part of the page.
  findFallbackInputGroups(key) {
    const root = this.element.closest("form") || document
    const esc = CSS.escape(key)
    const matches = Array.from(
      root.querySelectorAll(
        `[name="custom_fields[${esc}]"],[name="custom_fields[${esc}][]"],` +
          `[name$="[custom_fields][${esc}]"],[name$="[custom_fields][${esc}][]"]`,
      ),
    )

    const groups = new Map()
    matches.forEach((input) => {
      const prefix = input.name.replace(/\[\]$/, "").replace(/\[[^[\]]*\]$/, "")
      if (!groups.has(prefix)) groups.set(prefix, [])
      groups.get(prefix).push(input)
    })
    return Array.from(groups.values())
  }

  findSourceInputs(root, prefix, key) {
    return Array.from(
      root.querySelectorAll(
        `[name="${prefix}[${CSS.escape(key)}]"],` + `[name="${prefix}[${CSS.escape(key)}][]"]`,
      ),
    )
  }

  readInputsValue(inputs) {
    const checkboxes = inputs.filter((i) => i.type === "checkbox")
    if (checkboxes.length > 0) {
      // Multiple checkboxes => array of checked values; single => "true"/null.
      const checked = checkboxes.filter((i) => i.checked)
      if (checkboxes.length === 1) return checked.length ? "true" : null
      return checked.map((i) => i.value)
    }

    const radios = inputs.filter((i) => i.type === "radio")
    if (radios.length > 0) {
      const selected = radios.find((i) => i.checked)
      return selected ? selected.value : null
    }

    const select = inputs.find((i) => i.tagName === "SELECT" && i.multiple)
    if (select) {
      return Array.from(select.selectedOptions).map((o) => o.value)
    }

    return inputs[0].value
  }

  isPresent(value) {
    if (Array.isArray(value)) return value.some((v) => String(v ?? "").trim() !== "")
    return String(value ?? "").trim() !== ""
  }

  valuesEqual(stored, expected) {
    if (Array.isArray(stored)) return stored.map(String).includes(String(expected))
    return String(stored ?? "") === String(expected ?? "")
  }

  valueContains(stored, expected) {
    if (Array.isArray(stored)) return stored.map(String).includes(String(expected))
    return String(stored ?? "").includes(String(expected ?? ""))
  }
}
